import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";
import { withAlert } from "react-alert";
import styled from "styled-components";
import { withTranslation } from "react-i18next";
import Dialog from "../../../waybee-ui/Dialog";
import DialogTitle from "../../../waybee-ui/Dialog/DialogTitle";
import DialogContent from "../../../waybee-ui/Dialog/DialogContent";
import DialogActions from "../../../waybee-ui/Dialog/DialogActions";
import Button from "../../../waybee-ui/Button";
import AclManagementService from "../../../services/AclManagementService";
import PermissionTree from "./PermissionTree";
import {
  SectionHeader,
  SectionHint,
  SectionTitle
} from "../../../components/CheckboxTree/CheckboxTreeStyles";
import { buildPermissionTree, getCounterpartCode } from "./buildPermissionTree";

const RM_NAME_MAX_LENGTH = 40;
const RM_CODE_MAX_LENGTH = 15;

const DialogSubtitle = styled(Typography)`
  && {
    color: #6b6b6b;
    font-size: 14px;
    margin: -8px 0 20px;
  }
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  & > * + * {
    margin-left: 4px;
  }
`;

const FormCard = styled.div`
  border: 1px solid #ececec;
  border-radius: 8px;
  background: #fff;
  padding: 16px;
`;

const CompactField = styled(TextField)`
  && {
    margin-top: 0;
    margin-bottom: 0;
  }
`;

const FieldsGrid = styled(Grid)`
  && {
    .MuiGrid-item {
      padding-top: 6px;
      padding-bottom: 6px;
    }
  }
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

function collectPermissionIds(nodes = []) {
  const ids = [];
  nodes.forEach(node => {
    (node.permissions || []).forEach(permission => {
      ids.push(permission.id);
    });
    ids.push(...collectPermissionIds(node.children || []));
  });
  return ids;
}

class RoleFormDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      name: "",
      description: "",
      counterpart: "",
      selectedIds: new Set(),
      initialPermissionIds: [],
      tree: [],
      allPermissionIds: [],
      loading: false,
      saving: false
    };
  }

  componentDidMount() {
    this.bootstrap();
  }

  componentDidUpdate(prevProps) {
    const { role, open } = this.props;
    if (prevProps.role !== role || prevProps.open !== open) {
      if (open) {
        this.bootstrap();
      }
    }
  }

  bootstrap = async () => {
    const { role, permissions, alert, t } = this.props;
    this.setState({ loading: true });
    try {
      const tree = buildPermissionTree(permissions);
      const allPermissionIds = collectPermissionIds(tree);
      let selectedIds = new Set();
      let initialPermissionIds = [];

      if (role && role.id) {
        const rolePermissions = await AclManagementService.getRolePermissions(
          role.id
        );
        initialPermissionIds = (rolePermissions || []).map(item => item.id);
        selectedIds = new Set(initialPermissionIds);
      }

      this.setState({
        name: (role && role.name) || "",
        description: (role && role.description) || "",
        counterpart: getCounterpartCode(role),
        selectedIds,
        initialPermissionIds,
        tree,
        allPermissionIds,
        loading: false
      });
    } catch (e) {
      console.error(e);
      alert.show(t("settings.role.loadPermissionsError"), {
        title: t("common.actions.error")
      });
      this.setState({ loading: false });
    }
  };

  handleTogglePermission = permissionId => {
    this.setState(prev => {
      const selectedIds = new Set(prev.selectedIds);
      if (selectedIds.has(permissionId)) {
        selectedIds.delete(permissionId);
      } else {
        selectedIds.add(permissionId);
      }
      return { selectedIds };
    });
  };

  selectAllPermissions = () => {
    this.setState(prev => ({
      selectedIds: new Set(prev.allPermissionIds)
    }));
  };

  clearPermissions = () => {
    this.setState({ selectedIds: new Set() });
  };

  handleSave = async () => {
    const { role, onSaved, alert, onClose, t } = this.props;
    const {
      name,
      description,
      counterpart,
      selectedIds,
      initialPermissionIds
    } = this.state;

    if (!name.trim()) {
      alert.show(t("settings.role.nameRequired"), {
        title: t("common.actions.attention")
      });
      return;
    }
    if (name.trim().length > RM_NAME_MAX_LENGTH) {
      alert.show(t("settings.role.nameMaxLength"), {
        title: t("common.actions.attention")
      });
      return;
    }
    if (!counterpart.trim()) {
      alert.show(t("settings.role.codeRequired"), {
        title: t("common.actions.attention")
      });
      return;
    }
    if (counterpart.trim().length > RM_CODE_MAX_LENGTH) {
      alert.show(t("settings.role.codeMaxLength"), {
        title: t("common.actions.attention")
      });
      return;
    }

    this.setState({ saving: true });
    try {
      const permissionIds = Array.from(selectedIds);

      if (role && role.id) {
        const initialSet = new Set(initialPermissionIds);
        const newPermissions = permissionIds
          .filter(id => !initialSet.has(id))
          .map(id => ({ id }));
        const removedPermissions = initialPermissionIds
          .filter(id => !selectedIds.has(id))
          .map(id => ({ id }));

        await AclManagementService.updateRole({
          id: role.id,
          name: name.trim(),
          description: description.trim(),
          counterpart: counterpart.trim(),
          newPermissions,
          removedPermissions
        });
      } else {
        await AclManagementService.saveRole({
          name: name.trim(),
          description: description.trim(),
          counterpart: counterpart.trim(),
          permissions: permissionIds
        });
      }

      alert.show(t("settings.role.saved"), {
        title: t("common.actions.success")
      });
      onSaved();
      onClose();
    } catch (e) {
      console.error(e);
      const message =
        (e.response &&
          e.response.data &&
          e.response.data.error &&
          e.response.data.error.message) ||
        t("settings.role.saveError");
      alert.show(message, { title: t("common.actions.error") });
    } finally {
      this.setState({ saving: false });
    }
  };

  render() {
    const { open, onClose, role, t } = this.props;
    const {
      name,
      description,
      counterpart,
      selectedIds,
      tree,
      allPermissionIds,
      loading,
      saving
    } = this.state;
    const isSystem = !!(role && role.isSystem);
    const isEditing = !!(role && role.id);
    const selectedCount = selectedIds.size;
    const totalCount = allPermissionIds.length;

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth closeButton>
        <DialogTitle>
          {isEditing ? t("settings.role.edit") : t("settings.role.new")}
        </DialogTitle>
        <DialogContent>
          <DialogSubtitle>{t("settings.role.subtitle")}</DialogSubtitle>

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {(isSystem || isEditing) && (
                <BadgeRow>
                  {isEditing && (
                    <Chip size="small" label={t("settings.role.editingChip")} />
                  )}
                  {isSystem && (
                    <Chip
                      size="small"
                      color="primary"
                      label={t("settings.profiles.systemChip")}
                    />
                  )}
                </BadgeRow>
              )}

              <Section>
                <SectionHeader>
                  <div>
                    <SectionTitle>{t("settings.role.data")}</SectionTitle>
                    <SectionHint>{t("settings.role.dataHint")}</SectionHint>
                  </div>
                </SectionHeader>
                <FormCard>
                  <FieldsGrid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <CompactField
                        label={t("common.labels.name")}
                        value={name}
                        onChange={e =>
                          this.setState({
                            name: e.target.value.slice(0, RM_NAME_MAX_LENGTH)
                          })}
                        fullWidth
                        disabled={isSystem}
                        required
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: RM_NAME_MAX_LENGTH }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CompactField
                        label={t("settings.role.code")}
                        value={counterpart}
                        onChange={e =>
                          this.setState({
                            counterpart: e.target.value.slice(
                              0,
                              RM_CODE_MAX_LENGTH
                            )
                          })}
                        fullWidth
                        disabled={isSystem}
                        required
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: RM_CODE_MAX_LENGTH }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CompactField
                        label={t("common.labels.description")}
                        value={description}
                        onChange={e =>
                          this.setState({ description: e.target.value })}
                        fullWidth
                        multiline
                        rows={2}
                        variant="outlined"
                        size="small"
                        placeholder={t("settings.role.descPlaceholder")}
                      />
                    </Grid>
                  </FieldsGrid>
                </FormCard>
              </Section>

              <Section>
                <SectionHeader>
                  <div>
                    <SectionTitle>
                      {t("settings.role.permissions")}
                    </SectionTitle>
                    <SectionHint>
                      {t("settings.role.selectedCount", {
                        selected: selectedCount,
                        total: totalCount
                      })}
                    </SectionHint>
                  </div>
                  <SectionActions>
                    <Button
                      color="secondary"
                      size="small"
                      onClick={this.selectAllPermissions}
                      disabled={
                        totalCount === 0 || selectedCount === totalCount
                      }
                    >
                      {t("settings.role.selectAll")}
                    </Button>
                    {selectedCount > 0 && (
                      <Button
                        color="secondary"
                        size="small"
                        onClick={this.clearPermissions}
                      >
                        {t("common.actions.clear")}
                      </Button>
                    )}
                  </SectionActions>
                </SectionHeader>
                <PermissionTree
                  tree={tree}
                  selectedIds={selectedIds}
                  onTogglePermission={this.handleTogglePermission}
                />
              </Section>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={onClose} disabled={saving}>
            {t("common.actions.cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={this.handleSave}
            disabled={loading || saving}
          >
            {saving ? t("common.actions.saving") : t("settings.role.save")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

RoleFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  role: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    isSystem: PropTypes.bool,
    counterpart: PropTypes.shape({
      externalRoleCounterpartId: PropTypes.string
    })
  }),
  permissions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  t: PropTypes.func.isRequired
};

RoleFormDialog.defaultProps = {
  role: null
};

export default withAlert()(withTranslation()(RoleFormDialog));
